import { Copy } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { mcpSetupPrompt, type McpPromptMode } from '../../lib/mcp-prompt';
import { Button } from '../../ui/Button';
import { Card } from '../../ui/misc';

/** MCP connection guide with a ready-to-paste prompt that lets the person's AI agent do the setup itself. */
export function McpSetupGuide({
  endpoint,
  instructions,
  mode,
  children,
}: {
  endpoint: string;
  instructions: string;
  mode: McpPromptMode;
  children?: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const [showing, setShowing] = useState(false);
  const prompt = mcpSetupPrompt({ endpoint, instructions, mode });
  const copy = () =>
    navigator.clipboard.writeText(prompt).then(
      () => setCopied(true),
      () => setShowing(true),
    );
  return (
    <Card
      title="MCP setup guide"
      subtitle="Copy the prompt into your AI agent (Claude Code, Codex, Antigravity…) and it sets up the connection and learns the working rules."
      action={
        <Button size="sm" icon={<Copy size={14} />} onClick={copy}>
          {copied ? 'Copied' : 'Copy prompt'}
        </Button>
      }
    >
      <div className="stack sm">
        <span className="small">
          Endpoint <span className="mono">{endpoint}</span> · Streamable HTTP
        </span>
        {children}
        <button
          type="button"
          className="btn btn-ghost sm"
          style={{ alignSelf: 'flex-start' }}
          aria-expanded={showing}
          onClick={() => setShowing((v) => !v)}
        >
          {showing ? 'Hide prompt' : 'Show prompt'}
        </button>
        {showing && (
          <pre
            className="mono small"
            role="region"
            aria-label="MCP setup prompt"
            tabIndex={0}
            style={{ whiteSpace: 'pre-wrap', margin: 0, maxHeight: 360, overflow: 'auto' }}
          >
            {prompt}
          </pre>
        )}
      </div>
    </Card>
  );
}
